import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Sparkles, BadgePercent, Check, ShieldCheck, ArrowRight, Loader2,
  AlertCircle, CheckCircle2, ChevronDown,
} from "lucide-react";
import Logo from "../components/Logo";
import client from "../lib/api";
import { getTryLanding } from "../lib/api";

const buyTryTier = (payload) =>
  client.post("/try-landing/buy", payload).then((r) => r.data);

export default function TryLandingPage() {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openTier, setOpenTier] = useState(null); // tier_key
  const [form, setForm] = useState({ customer_email: "", customer_name: "", customer_phone: "", domain_name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await getTryLanding();
        setDoc(d);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load this page.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isValidDomain = (s) => /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z]{2,})+$/i.test((s || "").trim());

  const submit = async (tierKey) => {
    setError("");
    if (!form.customer_email || !form.customer_email.includes("@")) {
      setError("Please enter a valid email");
      return;
    }
    if (!form.domain_name || !isValidDomain(form.domain_name)) {
      setError("Please enter a valid domain (e.g. yoursite.com)");
      return;
    }
    setSubmitting(true);
    try {
      const r = await buyTryTier({
        tier_key: tierKey,
        customer_email: form.customer_email.trim(),
        customer_name: form.customer_name.trim() || undefined,
        customer_phone: form.customer_phone.trim() || undefined,
        domain_name: form.domain_name.trim().toLowerCase(),
      });
      if (r?.token) {
        navigate(`/checkout?token=${r.token}`);
      } else {
        setError("Could not create payment link. Please try again.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="lm-font min-h-screen flex items-center justify-center bg-[#FFFBF6]">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF6B3D]" />
      </div>
    );
  }

  if (!doc || doc.enabled === false) {
    return (
      <div className="lm-font min-h-screen flex items-center justify-center bg-[#FFFBF6] p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-10 h-10 text-[#B23B12] mx-auto mb-3" />
          <p className="text-[#11151C] font-bold text-lg">This page is currently disabled.</p>
          <Link to="/" className="lm-btn-primary mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold">
            Back to Live Mover
          </Link>
        </div>
      </div>
    );
  }

  const hero = doc.hero || {};
  const heroIsDark = (hero.background_style || "coral") === "charcoal";

  return (
    <div className="lm-font min-h-screen bg-[#FFFBF6]" data-testid="try-landing-page">
      {/* Top nav */}
      <header className="border-b border-[#11151C]/10 bg-white">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <Link to="/"><Logo /></Link>
          <a href="#offer" className="text-sm font-bold text-[#11151C] hover:text-[#FF6B3D]">See the offer →</a>
        </div>
      </header>

      {/* Hero */}
      <section
        className={`px-4 py-16 sm:py-20 ${heroIsDark ? "bg-[#11151C] text-white" : "lm-hero-bg"}`}
        data-testid="try-hero"
      >
        <div className="max-w-4xl mx-auto text-center">
          {hero.eyebrow && (
            <div className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full ${
              heroIsDark ? "bg-[#FF6B3D] text-white" : "bg-[#FFE6D6] text-[#B23B12]"
            }`}>
              <Sparkles className="w-3.5 h-3.5" /> {hero.eyebrow}
            </div>
          )}
          <h1 className={`mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] ${heroIsDark ? "text-white" : "text-[#11151C]"}`}>
            {hero.title || "Locked promo pricing for life"}
          </h1>
          {hero.subtitle && (
            <p className={`mt-5 text-base sm:text-lg max-w-2xl mx-auto ${heroIsDark ? "text-white/75" : "text-[#11151C]/70"}`}>
              {hero.subtitle}
            </p>
          )}
          <a
            href="#offer"
            className="lm-btn-primary mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold"
            data-testid="try-hero-cta"
          >
            {hero.cta_text || "See the offer"} <ArrowRight className="w-4 h-4" />
          </a>
          {doc.trust_line && (
            <p className={`mt-5 text-xs ${heroIsDark ? "text-white/55" : "text-[#11151C]/55"}`}>
              <ShieldCheck className="inline w-3.5 h-3.5 mr-1 -mt-0.5" /> {doc.trust_line}
            </p>
          )}
        </div>
      </section>

      {/* Pricing tiers */}
      <section id="offer" className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#11151C]">Lock your price today</h2>
          <p className="text-sm text-[#11151C]/65 mt-2">The price you pay now is the price you&apos;ll renew at — every year, forever.</p>
        </div>

        {error && (
          <div className="max-w-xl mx-auto mb-6 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 inline-flex items-start gap-2" data-testid="try-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5">
          {(doc.tiers || []).map((t) => {
            const isOpen = openTier === t.key;
            const highlight = !!t.highlight;
            return (
              <div
                key={t.key}
                className={`bg-white rounded-2xl border p-6 flex flex-col ${
                  highlight ? "border-[#FF6B3D] shadow-lg shadow-[#FF6B3D]/15 -translate-y-1" : "border-[#11151C]/10"
                }`}
                data-testid={`try-tier-${t.key}`}
              >
                {highlight && (
                  <div className="self-start text-[10px] font-bold uppercase tracking-wider bg-[#FF6B3D] text-white px-2 py-0.5 rounded-full">
                    Most popular
                  </div>
                )}
                <h3 className="text-xl font-extrabold text-[#11151C] mt-2">{t.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-[#11151C]">₹{Number(t.price).toLocaleString("en-IN")}</span>
                  <span className="text-sm text-[#11151C]/55">/yr</span>
                </div>
                <div className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#B23B12]">
                  <BadgePercent className="w-3.5 h-3.5" /> Renews at ₹{Number(t.locked_renewal_price || t.price).toLocaleString("en-IN")} forever
                </div>
                <ul className="mt-5 space-y-2 text-sm text-[#11151C]/80">
                  {(t.feature_lines || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 text-[#FF6B3D] shrink-0" /> {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setOpenTier(isOpen ? null : t.key)}
                  className={`mt-6 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold ${
                    highlight ? "lm-btn-primary" : "border border-[#FF6B3D] text-[#B23B12] hover:bg-[#FFE6D6]"
                  }`}
                  data-testid={`try-tier-cta-${t.key}`}
                >
                  {isOpen ? "Hide form" : `Get ${t.name} →`}
                </button>

                {isOpen && (
                  <div className="mt-4 pt-4 border-t border-[#11151C]/10 space-y-2">
                    <input
                      type="email"
                      placeholder="Your email"
                      value={form.customer_email}
                      onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                      data-testid={`try-form-email-${t.key}`}
                      className="w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Your name (optional)"
                      value={form.customer_name}
                      onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                      className="w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm"
                    />
                    <input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={form.customer_phone}
                      onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Your domain (e.g. mybiz.in)"
                      value={form.domain_name}
                      onChange={(e) => setForm({ ...form, domain_name: e.target.value.toLowerCase().trim() })}
                      data-testid={`try-form-domain-${t.key}`}
                      className="w-full px-3 py-2 border border-[#11151C]/15 rounded-xl text-sm"
                    />
                    <button
                      onClick={() => submit(t.key)}
                      disabled={submitting}
                      data-testid={`try-form-submit-${t.key}`}
                      className="w-full lm-btn-primary inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold disabled:opacity-60"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                      Continue to payment
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      {(doc.faqs || []).length > 0 && (
        <section className="max-w-3xl mx-auto px-4 pb-20">
          <h2 className="text-2xl font-extrabold text-[#11151C] text-center mb-6">Common questions</h2>
          <div className="space-y-2">
            {doc.faqs.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="bg-white border border-[#11151C]/10 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(open ? null : i)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left font-semibold text-[#11151C] hover:bg-[#FFFBF6]"
                    data-testid={`try-faq-${i}`}
                  >
                    {f.q}
                    <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && <div className="px-4 pb-4 text-sm text-[#11151C]/70">{f.a}</div>}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <footer className="border-t border-[#11151C]/10 py-6 text-center text-xs text-[#11151C]/50">
        © Live Mover. <Link to="/" className="hover:text-[#FF6B3D]">Visit main site</Link>
      </footer>
    </div>
  );
}
